import React, { useContext, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import api from '../services/api';
import { getProfilePhotoUrl } from '../config';
import AnnouncementModal from './AnnouncementModal';

const Navbar = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Charger le nombre de notifications non lues
  useEffect(() => {
    if (user) {
      const fetchUnread = async () => {
        try {
          const res = await api.get('/notifications');
          const notifications = Array.isArray(res.data) ? res.data : [];
          const unread = notifications.filter(n => !n.lu).length;
          setUnreadCount(unread);
        } catch (err) {
          console.error('Erreur notifications:', err);
        }
      };
      fetchUnread();
      // Rafraîchir toutes les 30 secondes
      const interval = setInterval(fetchUnread, 30000);
      return () => clearInterval(interval);
    }
  }, [user, location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const NavLink = ({ to, children, icon }) => (
    <Link
      to={to}
      className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
        isActive(to)
          ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md shadow-indigo-200'
          : 'text-gray-600 hover:bg-gray-100 hover:text-indigo-600'
      }`}
      onClick={() => setMobileMenuOpen(false)}
    >
      {icon}
      <span>{children}</span>
    </Link>
  );

  return (
    <nav className="bg-white/80 backdrop-blur-lg border-b border-gray-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img 
              src="/logo.png" 
              alt="DJAMANN" 
              className="w-11 h-11 rounded-2xl shadow-lg shadow-indigo-200 group-hover:shadow-xl group-hover:scale-105 transition-all duration-300 object-contain"
            />
            <div className="hidden sm:block">
              <span className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                DJAMANN
              </span>
              <p className="text-[10px] text-gray-400 -mt-1">Votre business, zéro barrière</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-2">
            {user && (
              <>
                <NavLink to="/" icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                }>
                  'Accueil'
                </NavLink>
                
                <NavLink to="/projects" icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                }>
                  'Projets'
                </NavLink>
                
                <NavLink to="/services" icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                }>
                  'Services'
                </NavLink>
                
                <NavLink to="/my-applications" icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                }>
                  'Candidatures'
                </NavLink>

                {(user.role === 'author' || user.role === 'admin') && (
                  <NavLink to="/author" icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  }>
                    'Mes Projets'
                  </NavLink>
                )}

                {user.role === 'admin' && (
                  <NavLink to="/admin" icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  }>
                    'Admin'
                  </NavLink>
                )}
              </>
            )}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {user ? (
              <div className="flex items-center gap-2">
                {/* Notifications */}
                <Link 
                  to="/notifications" 
                  className="relative p-2.5 rounded-xl text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 transition-all duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Link>

                {/* Messages */}
                <Link 
                  to="/my-service-requests" 
                  className="relative p-2.5 rounded-xl text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 transition-all duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </Link>

                {/* Profile Dropdown */}
                <div className="relative">
                  <button 
                    onClick={() => setProfileOpen(!profileOpen)}
                    className="flex items-center gap-2 p-1.5 pl-1.5 pr-3 rounded-2xl hover:bg-gray-100 transition-all duration-200 border border-transparent hover:border-gray-200"
                  >
                    {user.photo_profil ? (
                      <img 
                        src={getProfilePhotoUrl(user.photo_profil)}
                        alt={user.nom}
                        className="w-9 h-9 rounded-xl object-cover"
                      />
                    ) : (
                      <div className="w-9 h-9 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center shadow-inner">
                        <span className="text-white font-bold text-sm">{user.nom?.[0]?.toUpperCase() || 'U'}</span>
                      </div>
                    )}
                    <span className="hidden md:block text-gray-700 font-medium text-sm max-w-24 truncate">{user.nom}</span>
                    <svg className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${profileOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* Dropdown Menu - Utilise un portail pour mobile */}
                  {profileOpen && ReactDOM.createPortal(
                    <div className="fixed inset-0" style={{ zIndex: 9999 }}>
                      {/* Backdrop */}
                      <div 
                        className="absolute inset-0 bg-black/40 lg:bg-black/10" 
                        onClick={() => setProfileOpen(false)}
                      ></div>
                      
                      {/* Menu - Bottom sheet sur mobile, dropdown sur desktop */}
                      <div className="absolute bottom-0 left-0 right-0 lg:bottom-auto lg:left-auto lg:top-16 lg:right-4 lg:w-72">
                        <div className="bg-white rounded-t-3xl lg:rounded-2xl shadow-2xl max-h-[85vh] lg:max-h-[80vh] overflow-hidden">
                          {/* Mobile Header with close button */}
                          <div className="lg:hidden flex items-center justify-between px-4 pt-4 pb-3 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-purple-50">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center text-white font-bold">
                                {user.nom?.charAt(0)?.toUpperCase() || 'U'}
                              </div>
                              <div>
                                <p className="font-semibold text-gray-800">{user.nom}</p>
                                <p className="text-xs text-gray-500">{user.email}</p>
                              </div>
                            </div>
                            <button 
                              onClick={() => setProfileOpen(false)}
                              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/80 text-gray-500 hover:bg-white shadow-sm"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                          
                          {/* Desktop User Info Header */}
                          <div className="hidden lg:block px-4 py-3 border-b border-gray-100">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center text-white font-bold text-base">
                                {user.nom?.charAt(0)?.toUpperCase() || 'U'}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-gray-800 truncate">{user.nom}</p>
                                <p className="text-sm text-gray-500 truncate">{user.email}</p>
                              </div>
                              <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full capitalize flex-shrink-0">
                                {user.role}
                              </span>
                            </div>
                          </div>

                          {/* Scrollable menu items */}
                          <div className="overflow-y-auto max-h-[60vh] lg:max-h-[50vh] overscroll-contain">
                            {/* Mobile role badge */}
                            <div className="lg:hidden px-4 py-2 bg-gray-50 border-b border-gray-100">
                              <span className="px-3 py-1 bg-indigo-100 text-indigo-700 text-sm font-medium rounded-full capitalize">
                                {user.role}
                              </span>
                            </div>
                            
                            <div className="py-2 px-2 lg:px-0">
                              <Link 
                                to="/profile" 
                                className="flex items-center gap-3 px-3 lg:px-4 py-3 lg:py-2.5 text-gray-700 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 active:bg-indigo-100 transition-all duration-200 rounded-xl lg:rounded-none"
                                onClick={() => setProfileOpen(false)}
                              >
                                <div className="w-10 h-10 lg:w-8 lg:h-8 bg-gray-100 rounded-xl lg:rounded-lg flex items-center justify-center flex-shrink-0">
                                  <svg className="w-5 h-5 lg:w-4 lg:h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                </div>
                                <span className="font-medium">Mon profil</span>
                              </Link>

                              <Link 
                                to="/my-applications" 
                                className="flex items-center gap-3 px-3 lg:px-4 py-3 lg:py-2.5 text-gray-700 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 active:bg-indigo-100 transition-all duration-200 rounded-xl lg:rounded-none"
                                onClick={() => setProfileOpen(false)}
                              >
                                <div className="w-10 h-10 lg:w-8 lg:h-8 bg-blue-100 rounded-xl lg:rounded-lg flex items-center justify-center flex-shrink-0">
                                  <svg className="w-5 h-5 lg:w-4 lg:h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                  </svg>
                                </div>
                                <span className="font-medium">Mes candidatures</span>
                              </Link>

                              <Link 
                                to="/my-service-requests" 
                                className="flex items-center gap-3 px-3 lg:px-4 py-3 lg:py-2.5 text-gray-700 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 active:bg-indigo-100 transition-all duration-200 rounded-xl lg:rounded-none"
                                onClick={() => setProfileOpen(false)}
                              >
                                <div className="w-10 h-10 lg:w-8 lg:h-8 bg-green-100 rounded-xl lg:rounded-lg flex items-center justify-center flex-shrink-0">
                                  <svg className="w-5 h-5 lg:w-4 lg:h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                </div>
                                <span className="font-medium text-sm lg:text-base">Mes demandes de services</span>
                              </Link>

                              {(user.role === 'author' || user.role === 'admin') && (
                                <Link 
                                  to="/received-applications" 
                                  className="flex items-center gap-3 px-3 lg:px-4 py-3 lg:py-2.5 text-gray-700 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 active:bg-indigo-100 transition-all duration-200 rounded-xl lg:rounded-none"
                                  onClick={() => setProfileOpen(false)}
                                >
                                  <div className="w-10 h-10 lg:w-8 lg:h-8 bg-amber-100 rounded-xl lg:rounded-lg flex items-center justify-center flex-shrink-0">
                                    <svg className="w-5 h-5 lg:w-4 lg:h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                    </svg>
                                  </div>
                                  <span className="font-medium">Candidatures reçues</span>
                                </Link>
                              )}

                              {(user.role === 'author' || user.role === 'admin') && (
                                <Link 
                                  to="/workspace" 
                                  className="flex items-center gap-3 px-3 lg:px-4 py-3 lg:py-2.5 text-gray-700 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 active:bg-indigo-100 transition-all duration-200 rounded-xl lg:rounded-none"
                                  onClick={() => setProfileOpen(false)}
                                >
                                  <div className="w-10 h-10 lg:w-8 lg:h-8 bg-indigo-100 rounded-xl lg:rounded-lg flex items-center justify-center flex-shrink-0">
                                    <span className="text-xl lg:text-sm">💼</span>
                                  </div>
                                  <span className="font-medium">Mon Espace Business</span>
                                </Link>
                              )}

                              {user.role === 'admin' && (
                                <Link 
                                  to="/admin/service-requests" 
                                  className="flex items-center gap-3 px-3 lg:px-4 py-3 lg:py-2.5 text-gray-700 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 active:bg-indigo-100 transition-all duration-200 rounded-xl lg:rounded-none"
                                  onClick={() => setProfileOpen(false)}
                                >
                                  <div className="w-10 h-10 lg:w-8 lg:h-8 bg-purple-100 rounded-xl lg:rounded-lg flex items-center justify-center flex-shrink-0">
                                    <svg className="w-5 h-5 lg:w-4 lg:h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                    </svg>
                                  </div>
                                  <span className="font-medium">Gestion des demandes</span>
                                </Link>
                              )}

                              <div className="border-t border-gray-100 my-2 mx-2 lg:mx-0"></div>

                              <Link 
                                to="/my-subscription" 
                                className="flex items-center gap-3 px-3 lg:px-4 py-3 lg:py-2.5 text-gray-700 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 active:bg-indigo-100 transition-all duration-200 rounded-xl lg:rounded-none"
                                onClick={() => setProfileOpen(false)}
                              >
                                <div className="w-10 h-10 lg:w-8 lg:h-8 bg-emerald-100 rounded-xl lg:rounded-lg flex items-center justify-center flex-shrink-0">
                                  <span className="text-xl lg:text-sm">📦</span>
                                </div>
                                <span className="font-medium">Mon Abonnement</span>
                              </Link>

                              <Link 
                                to="/guide" 
                                className="flex items-center gap-3 px-3 lg:px-4 py-3 lg:py-2.5 text-gray-700 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 active:bg-indigo-100 transition-all duration-200 rounded-xl lg:rounded-none"
                                onClick={() => setProfileOpen(false)}
                              >
                                <div className="w-10 h-10 lg:w-8 lg:h-8 bg-teal-100 rounded-xl lg:rounded-lg flex items-center justify-center flex-shrink-0">
                                  <span className="text-xl lg:text-sm">📖</span>
                                </div>
                                <span className="font-medium">Guide d'utilisation</span>
                              </Link>

                              <Link 
                                to="/settings" 
                                className="flex items-center gap-3 px-3 lg:px-4 py-3 lg:py-2.5 text-gray-700 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 active:bg-indigo-100 transition-all duration-200 rounded-xl lg:rounded-none"
                                onClick={() => setProfileOpen(false)}
                              >
                                <div className="w-10 h-10 lg:w-8 lg:h-8 bg-gray-100 rounded-xl lg:rounded-lg flex items-center justify-center flex-shrink-0">
                                  <svg className="w-5 h-5 lg:w-4 lg:h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                </div>
                                <span className="font-medium">Paramètres</span>
                              </Link>
                            </div>
                          </div>

                          {/* Logout button - Fixed at bottom */}
                          <div className="border-t border-gray-100 p-3 lg:p-2 bg-gray-50/80">
                            <button
                              onClick={() => { handleLogout(); setProfileOpen(false); }}
                              className="flex items-center justify-center gap-3 px-4 py-3 lg:py-2.5 text-red-600 hover:bg-red-50 active:bg-red-100 w-full transition-all duration-200 rounded-xl"
                            >
                              <div className="w-10 h-10 lg:w-8 lg:h-8 bg-red-100 rounded-xl lg:rounded-lg flex items-center justify-center">
                                <svg className="w-5 h-5 lg:w-4 lg:h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                </svg>
                              </div>
                              <span className="font-medium">Déconnexion</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>,
                    document.body
                  )}
                </div>

                {/* Mobile Menu Button */}
                <button 
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="lg:hidden p-2.5 rounded-xl text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 transition-all duration-200"
                >
                  {mobileMenuOpen ? (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  )}
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link 
                  to="/login" 
                  className="px-5 py-2.5 text-indigo-600 font-semibold hover:bg-indigo-50 rounded-xl transition-all duration-200"
                >
                  'Connexion'
                </Link>
                <Link 
                  to="/register" 
                  className="px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-indigo-200 hover:-translate-y-0.5 transition-all duration-200"
                >
                  'Inscription'
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {user && mobileMenuOpen && (
        <div className="lg:hidden border-t border-gray-100 bg-white/95 backdrop-blur-lg animate-slideDown">
          <div className="px-4 py-4 space-y-2">
            <NavLink to="/" icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            }>
              'Accueil'
            </NavLink>

            <NavLink to="/projects" icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            }>
              'Projets'
            </NavLink>

            <NavLink to="/services" icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            }>
              'Services'
            </NavLink>

            <NavLink to="/my-applications" icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            }>
              'Candidatures'
            </NavLink>

            <NavLink to="/my-subscription" icon={
              <span className="text-base">📦</span>
            }>
              Mon Abonnement
            </NavLink>

            {(user.role === 'author' || user.role === 'admin') && (
              <NavLink to="/author" icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              }>
                'Mes Projets'
              </NavLink>
            )}

            {user.role === 'admin' && (
              <NavLink to="/admin" icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              }>
                'Admin'
              </NavLink>
            )}
          </div>
        </div>
      )}
      
      {/* Modal des annonces - affiché seulement si connecté */}
      {user && <AnnouncementModal />}
    </nav>
  );
};

export default Navbar;
